import React from 'react';
import { Box, Typography } from '@mui/material';
import { SemanticResult } from './PepWiseAIChatPopup.types';

const SEVERITY_COLORS: Record<string, { bg: string; color: string; border: string }> = {
    Critical: { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' },
    High: { bg: '#FFEDD5', color: '#9A3412', border: '#FED7AA' },
    Medium: { bg: '#FEF9C3', color: '#854D0E', border: '#FDE68A' },
    Low: { bg: '#DCFCE7', color: '#166534', border: '#BBF7D0' },
};

const getFieldValue = (inc: any, keys: string[]): any => {
    if (!inc || typeof inc !== 'object') return null;

    for (const key of keys) {
        if (inc[key] !== undefined && inc[key] !== null && inc[key] !== '') {
            return inc[key];
        }
    }

    const body =
        inc['pepwise.body'] ||
        inc.pepwise?.body ||
        inc._source?.['pepwise.body'] ||
        inc._source?.pepwise?.body;

    if (body && typeof body === 'object') {
        const snowAlerts = body['snow-alerts'] || body.snowAlerts;
        if (snowAlerts && typeof snowAlerts === 'object') {
            for (const key of keys) {
                if (snowAlerts[key] !== undefined && snowAlerts[key] !== null && snowAlerts[key] !== '') {
                    return snowAlerts[key];
                }
            }
        }

        const snowInc = body.servicenow?.incident || body.incident;
        if (snowInc && typeof snowInc === 'object') {
            for (const key of keys) {
                if (snowInc[key] !== undefined && snowInc[key] !== null && snowInc[key] !== '') {
                    return snowInc[key];
                }
            }
        }

        for (const key of keys) {
            if (body[key] !== undefined && body[key] !== null && body[key] !== '') {
                return body[key];
            }
        }
    }

    if (inc._source && typeof inc._source === 'object') {
        for (const key of keys) {
            if (inc._source[key] !== undefined && inc._source[key] !== null && inc._source[key] !== '') {
                return inc._source[key];
            }
        }
    }

    return null;
};

const getNormalizedSeverity = (rawSev: any): { text: string; style: { bg: string; color: string; border: string } } => {
    if (!rawSev) return { text: 'Low', style: SEVERITY_COLORS['Low'] };
    const str = String(rawSev).trim();
    if (str.includes('Critical') || str.startsWith('1')) {
        return { text: 'Critical', style: SEVERITY_COLORS['Critical'] };
    }
    if (str.includes('High') || str.startsWith('2')) {
        return { text: 'High', style: SEVERITY_COLORS['High'] };
    }
    if (str.includes('Medium')) {
        return { text: 'Medium', style: SEVERITY_COLORS['Medium'] };
    }
    if (str.includes('Low') || str.startsWith('3')) {
        return { text: 'Low', style: SEVERITY_COLORS['Low'] };
    }
    return { text: str, style: SEVERITY_COLORS['Low'] };
};

const formatStartTime = (rawTime: any): string => {
    if (!rawTime) return '—';
    try {
        const d = new Date(rawTime);
        if (isNaN(d.getTime())) return String(rawTime);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${mins}`;
    } catch {
        return String(rawTime);
    }
};

const formatDuration = (inc: any): string => {
    const rawDur = getFieldValue(inc, ['duration', 'duration_str', 'duration_ms', 'execution_ms']);
    if (rawDur) return String(rawDur);

    const openedAt = getFieldValue(inc, ['opened_at', 'openedAt', 'start_time', 'created_at']);
    const resolvedAt = getFieldValue(inc, ['resolved_at', 'closed_at', 'updated_at', 'sys_updated_on', '@timestamp']);

    if (openedAt && resolvedAt) {
        const d1 = new Date(openedAt).getTime();
        const d2 = new Date(resolvedAt).getTime();
        if (!isNaN(d1) && !isNaN(d2) && d2 >= d1) {
            const diffMins = Math.floor((d2 - d1) / 60000);
            if (diffMins < 60) return `${diffMins}m`;
            const hours = Math.floor(diffMins / 60);
            const remainingMins = diffMins % 60;
            if (hours < 24) return `${hours}h ${remainingMins}m`;
            const days = (hours / 24).toFixed(1);
            return `${days}d`;
        }
    }
    return '—';
};

const SemanticResultCard: React.FC<{ result: SemanticResult }> = ({ result }) => {
    const { visualization, title, data, metadata } = result;

    if (visualization === 'kpi_card') {
        const value =
            data?.value ??
            data?.raw?.failed_pipeline_count?.value ??
            data?.raw?.count?.value ??
            data?.raw?.total?.value;

        if (value == null) return null;

        const displayValue = value.toLocaleString();
        return (
            <Box sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: '#F0F4FF', border: '1px solid #C7D7F5' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    {title || 'Metric'}
                </Typography>
                <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#003771', lineHeight: 1 }}>
                    {displayValue}
                </Typography>
                {data?.unit && (
                    <Typography sx={{ fontSize: '12px', color: '#64748B', mt: 0.25 }}>{data.unit}</Typography>
                )}
            </Box>
        );
    }

    if (visualization === 'incident_list' || visualization === 'incident_detail_card' || visualization === 'table' || visualization === 'alert_list') {
        let incidents: any[] = [];
        if (Array.isArray(data)) {
            incidents = data;
        } else if (Array.isArray(data?.data)) {
            incidents = data.data;
        } else if (Array.isArray(data?.rows)) {
            incidents = data.rows;
        } else if (Array.isArray(data?.incidents)) {
            incidents = data.incidents;
        }

        // Only display table if rows > 3
        if (!incidents || incidents.length <= 3) return null;

        const totalCount = metadata?.total_count;
        const isTruncated = metadata?.truncated;

        return (
            <Box sx={{ mb: 1.5, borderRadius: '10px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                {/* Header */}
                <Box sx={{ px: 2, py: 1.25, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {title || 'Incidents'}
                    </Typography>
                    {totalCount != null && (
                        <Typography sx={{ fontSize: '11px', color: '#64748B', fontWeight: 500 }}>
                            {isTruncated ? `Showing ${incidents.length} of ${totalCount.toLocaleString()}` : `${totalCount.toLocaleString()} total`}
                        </Typography>
                    )}
                </Box>

                {/* Table container with horizontal scroll for popup mode */}
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: '500px' }}>
                        {/* Column headers */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 1.1fr 0.9fr 1.2fr 0.7fr', px: 2, py: 0.75, backgroundColor: '#F1F5F9', borderBottom: '1px solid #E2E8F0', gap: 0.5 }}>
                            {['Incident ID', 'Status', 'Severity', 'Start Time', 'Duration'].map((col) => (
                                <Typography key={col} sx={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                    {col}
                                </Typography>
                            ))}
                        </Box>

                        {/* Rows */}
                        {incidents.map((inc: any, idx: number) => {
                            const rawId = getFieldValue(inc, ['incident_id', 'number', 'incident_number', 'id', 'sys_id']) || '—';
                            const rawStatus = getFieldValue(inc, ['status', 'state', 'incident_state', 'state_name']) || '—';
                            const rawSev = getFieldValue(inc, ['severity_level', 'severity', 'priority', 'impact']);
                            const severityObj = getNormalizedSeverity(rawSev);
                            const rawTime = getFieldValue(inc, ['start_time', 'opened_at', 'openedAt', 'created_at', 'first_triggered_at', 'timestamp', '@timestamp']);
                            const startTimeStr = formatStartTime(rawTime);
                            const durationStr = formatDuration(inc);

                            return (
                                <Box
                                    key={rawId !== '—' ? rawId : idx}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1.3fr 1.1fr 0.9fr 1.2fr 0.7fr',
                                        px: 2, py: 1,
                                        gap: 0.5,
                                        alignItems: 'center',
                                        borderBottom: idx < incidents.length - 1 ? '1px solid #F1F5F9' : 'none',
                                        backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#FAFCFF',
                                        transition: 'background-color 0.15s',
                                        '&:hover': { backgroundColor: '#EFF6FF' },
                                    }}
                                >
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#003771', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        {rawId}
                                    </Typography>
                                    <Box>
                                        <Box sx={{
                                            display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px',
                                            backgroundColor: rawStatus === 'Active' || rawStatus === 'New' || rawStatus === 'In Progress' ? '#DCFCE7' : '#F1F5F9',
                                            border: `1px solid ${rawStatus === 'Active' || rawStatus === 'New' || rawStatus === 'In Progress' ? '#BBF7D0' : '#E2E8F0'}`,
                                        }}>
                                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: rawStatus === 'Active' || rawStatus === 'New' || rawStatus === 'In Progress' ? '#166534' : '#475569', whiteSpace: 'nowrap' }}>
                                                {rawStatus}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box sx={{
                                            display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px',
                                            backgroundColor: severityObj.style.bg, border: `1px solid ${severityObj.style.border}`,
                                        }}>
                                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: severityObj.style.color, whiteSpace: 'nowrap' }}>
                                                {severityObj.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontSize: '11px', color: '#475569', whiteSpace: 'nowrap' }}>
                                        {startTimeStr}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#64748B', whiteSpace: 'nowrap' }}>
                                        {durationStr}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    }

    return null;
};

export default SemanticResultCard;
